// TeamList (Domain/Schedule; was ScheduleViews' TeamList, BL-189): who's working today, one technician a
// row on hairlines: their pen and name, their state now as a word (red when late), what they're on and
// how full the day is, what they do, and the one action, their day (onPick). The one whose day shows
// beside the list has a heavier rule down its side and says Shown. Draws from ready-to-show words
// (schedule-schemas.ts' TeamListProps, as dispatch.board's rows give them). Reads only --teamlist-*;
// PenKey reads its own.
import type { TeamListProps } from '../../schedule-schemas.ts';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import './TeamList.css';

type Tech = TeamListProps['techs'][number];
/** A state's word, as DESIGN.md §2 draws it: muted for kind and state, dashed for waiting, red for a problem. */
export type TeamWordKind = 'muted' | 'wait' | 'bad' | 'now';

const KIND: Record<Tech['state'], TeamWordKind> = {
  working: 'now',
  driving: 'now',
  away: 'wait',
  free: 'muted',
  off: 'muted',
};

/** "45 min", "1 h", "2 h 30". */
export const duration = (minutes: number) => {
  const m = Math.max(0, Math.round(minutes));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}`;
};
export const visitsWord = (n: number) => `${n} ${n === 1 ? 'visit' : 'visits'}`;

export interface TeamListComponentProps {
  data: TeamListProps;
  /** The one whose day shows beside the list. */
  selected?: string | null;
  onPick?: (id: string) => void;
  /** What it says with nobody on the team; a page region that says its own passes null. */
  empty?: string | null;
}

export function TeamList({ data, selected, onPick, empty = 'No technicians yet.' }: TeamListComponentProps) {
  if (!data.techs.length) return empty === null ? null : <p className="fl-teamlist__empty">{empty}</p>;
  return (
    <ul className="fl-teamlist">
      {data.techs.map((t) => (
        <li key={t.id} className="fl-teamlist__row" data-on={t.id === selected ? '' : undefined}>
          <span className="fl-teamlist__main">
            <span className="fl-teamlist__who">
              <PenKey pen={(t.pen ?? null) as Pen | null}>
                <b className="fl-teamlist__name">{t.name}</b>
              </PenKey>{' '}
              <span className="fl-teamlist__word" data-kind={t.late ? 'bad' : KIND[t.state]}>
                {t.late ?? t.word}
              </span>
            </span>
            <span className="fl-teamlist__sub">
              {t.line}
              {/\.$/.test(t.line) ? '' : '.'}
              {/* a day off with nothing on it says only why */}
              {t.state === 'off' && !t.visits
                ? ''
                : ` ${visitsWord(t.visits)}, ${duration(t.bookedMinutes)} booked.`}
            </span>
            {t.skills.length > 0 && (
              <span className="fl-teamlist__sub">Does {t.skills.join(', ').toLowerCase()}</span>
            )}
          </span>
          {t.id === selected ? (
            <span className="fl-teamlist__shown">Shown</span>
          ) : onPick ? (
            <button
              type="button"
              className="fl-teamlist__pick"
              onClick={() => onPick(t.id)}
              aria-label={`${t.name}’s day`}
            >
              Their day
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
